import React, { useState, useMemo, useContext } from 'react';
import FilterSection from './FilterSection';
import PaySlipCard from './PaySlipCard';
import { PayrollContext } from '../../../context/PayrollContext';

const monthMetadata = [
  { month: 'January', monthNumber: 1, color: 'yellow', downloadIcon: 'https://api.builder.io/api/v1/image/assets/TEMP/d598e24b48cea345ff2af5827c845c907e28d9a1?placeholderIfAbsent=true&apiKey=ff7ebde024b045f5abc0e1f12d69fcae' },
  { month: 'February', monthNumber: 2, color: 'rose', downloadIcon: 'https://api.builder.io/api/v1/image/assets/TEMP/ce2298253c66d96d7ab7bc7684f1cf70ebd521eb?placeholderIfAbsent=true&apiKey=ff7ebde024b045f5abc0e1f12d69fcae' },
  { month: 'March', monthNumber: 3, color: 'teal', downloadIcon: 'https://api.builder.io/api/v1/image/assets/TEMP/9689e3e50b1bb7b0eb3517a5cf096524e43f08b2?placeholderIfAbsent=true&apiKey=ff7ebde024b045f5abc0e1f12d69fcae' },
  { month: 'April', monthNumber: 4, color: 'pink', downloadIcon: 'https://api.builder.io/api/v1/image/assets/TEMP/7c2f03554045aa6e43da18352dd8a0999d1c6b04?placeholderIfAbsent=true&apiKey=ff7ebde024b045f5abc0e1f12d69fcae' },
  { month: 'May', monthNumber: 5, color: 'lime', downloadIcon: 'https://api.builder.io/api/v1/image/assets/TEMP/2f37cbc5de39650a5bd839cbee7c765aeb1a3d42?placeholderIfAbsent=true&apiKey=ff7ebde024b045f5abc0e1f12d69fcae' },
  { month: 'June', monthNumber: 6, color: 'brown', downloadIcon: 'https://api.builder.io/api/v1/image/assets/TEMP/7b86635d66062267170841a2ae82daef7c4f58ba?placeholderIfAbsent=true&apiKey=ff7ebde024b045f5abc0e1f12d69fcae' },
  { month: 'July', monthNumber: 7, color: 'red', downloadIcon: 'https://api.builder.io/api/v1/image/assets/TEMP/80a7acbac0c2ed2adaae13c71f2de43f551fc30c?placeholderIfAbsent=true&apiKey=ff7ebde024b045f5abc0e1f12d69fcae' },
  { month: 'August', monthNumber: 8, color: 'blue', downloadIcon: 'https://api.builder.io/api/v1/image/assets/TEMP/8d59b9493b36e94a6778e62cd4ba6d0c3c2126e1?placeholderIfAbsent=true&apiKey=ff7ebde024b045f5abc0e1f12d69fcae' },
  { month: 'September', monthNumber: 9, color: 'emerald', downloadIcon: 'https://api.builder.io/api/v1/image/assets/TEMP/95afddd69840046ef69fa34582a2db65a4938625?placeholderIfAbsent=true&apiKey=ff7ebde024b045f5abc0e1f12d69fcae' },
  { month: 'October', monthNumber: 10, color: 'orange', downloadIcon: 'https://api.builder.io/api/v1/image/assets/TEMP/50479abbc23d0b7622f74d538fbb7054d92fb8da?placeholderIfAbsent=true&apiKey=ff7ebde024b045f5abc0e1f12d69fcae' },
  { month: 'November', monthNumber: 11, color: 'violet', downloadIcon: 'https://api.builder.io/api/v1/image/assets/TEMP/a1a0de13b50790e498d25117be7f431e8785cdbe?placeholderIfAbsent=true&apiKey=ff7ebde024b045f5abc0e1f12d69fcae' },
  { month: 'December', monthNumber: 12, color: 'crimson', downloadIcon: 'https://api.builder.io/api/v1/image/assets/TEMP/f27349b459be6f33deec9fe9764a1a75283a2975?placeholderIfAbsent=true&apiKey=ff7ebde024b045f5abc0e1f12d69fcae' },
];

const PaySlips = () => {
  const { payrolls, loading, error } = useContext(PayrollContext);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [searchTerm, setSearchTerm] = useState('');

const filteredPayslips = useMemo(() => {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;

  return payrolls
    .filter(payslip => {
      if (!payslip.payroll_date) return false;
      const [year, month] = payslip.payroll_date.split('-').map(Number);
      const matchesSearch = payslip.payroll_date.toLowerCase().includes(searchTerm.toLowerCase());
      const isAvailable = selectedYear < currentYear || (selectedYear === currentYear && month <= currentMonth);
      return matchesSearch && isAvailable && year === selectedYear;
    })
    .map(payslip => {
      const [year, month] = payslip.payroll_date.split('-').map(Number);
      const metadata = monthMetadata.find(m => m.monthNumber === month);

      // ── Map raw salary into earnings breakdown ──
      const salary = parseFloat(String(payslip.salary).replace(/[^\d.]/g, '')) || 0;
      const basicDA       = Math.round(salary * 0.50);
      const hra           = Math.round(salary * 0.20);
      const otherAllowance = salary - basicDA - hra;
      const paidDays      = payslip.paid_days ?? 26;
      const monthDays     = new Date(year, month, 0).getDate();
      const basicDAEarned = Math.round((basicDA / monthDays) * paidDays);
      const hraEarned     = Math.round((hra / monthDays) * paidDays);
      const otherEarned   = Math.round((otherAllowance / monthDays) * paidDays);
      const epf           = Math.round(basicDA * 0.12);
      const esi           = salary <= 21000 ? Math.round(salary * 0.0075) : 0;
      const pt            = salary > 15000 ? 200 : salary > 10000 ? 150 : 0;

      return {
        id: payslip.id,
        monthNumber: month,
        month: metadata?.month,
        year,
        color: metadata?.color,
        downloadIcon: metadata?.downloadIcon,
        isAvailable: true,
        payslipData: {
          empId:            String(payslip.emp_id ?? payslip.id ?? ''),
          employeeName:     payslip.employee_name ?? payslip.name ?? '',
          designation:      payslip.designation ?? '',
          location:         payslip.location ?? '',
          monthDays,
          paidDays,
          utrRef:           payslip.utr_ref ?? payslip.account_no ?? '',
          dateOfJoining:    payslip.joining_date ?? payslip.date_of_joining ?? '',
          earnings: {
            basicDA, basicDAEarned,
            hra, hraEarned,
            otherAllowance, otherAllowanceEarned: otherEarned,
            extraEarnings: 0, extraEarningsEarned: 0,
          },
          deductions: {
            epf, esi, pt,
            salaryAdvance: 0,
            insurance: 0,
          },
          netEarningsInWords: '',   // optionally add a number-to-words util here
        },
      };
    });
}, [payrolls, searchTerm, selectedYear]);

  const handleExport = () => console.log('Exporting payslips for year:', selectedYear);
  

  if (loading) return <p className="text-center py-20">Loading payslips...</p>;
  if (error) return <p className="text-center py-20 text-red-500">{error}</p>;

  return (
    <main className="flex flex-col px-5 pt-3.5 pb-8 w-full bg-sky-50 max-md:max-w-full">
      <h1 className="self-start text-2xl sm:text-3xl font-bold text-gray-800 mb-5">Pay Slips</h1>
      <FilterSection
        selectedYear={selectedYear}
        onYearChange={setSelectedYear}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onExport={handleExport}
      />
      <div className="mx-3.5 mt-6 max-md:mr-2.5 max-md:max-w-full">
        {filteredPayslips.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 w-full">
            {filteredPayslips.map(payslip => (
           

// ...

<PaySlipCard
  key={`${payslip.id}-${selectedYear}`}
  month={payslip.month}
  year={payslip.year}
  color={payslip.color}
  downloadIcon={payslip.downloadIcon}
  isAvailable={payslip.isAvailable}
  payslipData={payslip.payslipData}
  companyName="ALGORITHMICA CONSULTANCY SERVICES PRIVATE LIMITED"
  companyAddress="Urban 9 Level 4A/UG8F, Hyderabad - 500081, Telangana"
  companyLogoUrl="https://cdn.acsdev.in/fynryx/acs.png"   
/>
            ))}
          </div>
        ) : (
          <div className="flex justify-center items-center py-20">
            <div className="text-center text-gray-500">
              <p className="text-lg">No payslips found</p>
              <p className="text-sm mt-2">Try adjusting your search or year filter</p>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default PaySlips;
